import { AnalysisMarkdown, AnalysisSubHeader } from '../../../components';
import type { CulturalCardDto } from '@inference.engine/api-client';

interface CulturalMomentsProps {
  title: string;
  subtitle: string;
  moments: (string | CulturalCardDto)[];
}

/**
 * Cultural moments and trends as cards. Extraction sends bare prose today and the design
 * adds a title and an image on top, so a card renders whichever of the three it was handed
 * and closes up around the rest.
 */
export const CulturalMoments = ({ title, subtitle, moments }: CulturalMomentsProps) => {
  // One shape for the card to read, whichever of the two arrived. Every field is optional
  // because a report written before the redesign carries only the prose.
  const items: { title?: string; description?: string; imageUrl?: string }[] = moments.map(
    (moment) => (typeof moment === 'string' ? { description: moment } : moment),
  );

  if (items.length === 0) return null;

  return (
    <div className="flex flex-col gap-6">
      <AnalysisSubHeader title={title} subtitle={subtitle} subtitleTone="meta" />
      <div className="grid grid-cols-1 items-stretch gap-3 md:grid-cols-2 lg:grid-cols-3">
        {items.map((moment, i) => (
          <div
            key={`${moment.title ?? moment.description}-${i}`}
            className="flex h-full flex-col overflow-hidden rounded-xl border border-border-light"
          >
            {moment.imageUrl && (
              <img
                src={moment.imageUrl}
                alt={moment.title ?? ''}
                className="h-45 w-full shrink-0 object-cover"
              />
            )}
            <div className="flex flex-1 flex-col gap-3 p-xl">
              {moment.title && (
                <span className="text-body-xl font-medium text-foreground">{moment.title}</span>
              )}
              <AnalysisMarkdown
                text={moment.description}
                className="text-body-md text-foreground-alt"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
